import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AdminPage from '../../components/admin/AdminPage';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import SectionCard from '../../components/admin/SectionCard';
import { EmptyState, LoadingState } from '../../components/admin/StateViews';
import StatusChip from '../../components/admin/StatusChip';
import { Cause } from '../../types';
import { CauseService } from '../../services/CauseService';
import { AuditLogService } from '../../services/AuditLogService';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../context/ToastContext';

const PAGE_SIZE = 6;

type CauseAction = 'publish' | 'unpublish' | 'archive' | 'delete';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value || 0);

const CausesListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const canManage = ['SuperAdmin', 'Content-Manager'].includes(user?.role || '');
  const [causes, setCauses] = useState<Cause[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | Cause['status']>('all');
  const [page, setPage] = useState(1);
  const [pendingAction, setPendingAction] = useState<{
    type: CauseAction;
    cause: Cause;
  } | null>(null);

  const fetchCauses = async () => {
    try {
      setLoading(true);
      const data = await CauseService.getCauses();
      setCauses(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch causes. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCauses();
  }, []);

  const filteredCauses = useMemo(() => {
    return causes.filter((cause) => {
      const matchesSearch =
        cause.title.toLowerCase().includes(search.toLowerCase()) ||
        cause.titleEn.toLowerCase().includes(search.toLowerCase()) ||
        cause.titleAr.toLowerCase().includes(search.toLowerCase()) ||
        cause.description.toLowerCase().includes(search.toLowerCase()) ||
        cause.slug.toLowerCase().includes(search.toLowerCase()) ||
        cause.externalProgramId.toLowerCase().includes(search.toLowerCase()) ||
        String(cause.rank || '').includes(search.trim()) ||
        cause.mealType.toLowerCase().includes(search.toLowerCase());
      const matchesStatus =
        statusFilter === 'all' || cause.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [causes, search, statusFilter]);

  const paginatedCauses = filteredCauses.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );
  const pageCount = Math.max(1, Math.ceil(filteredCauses.length / PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const handleConfirmAction = async () => {
    if (!pendingAction) {
      return;
    }

    const { type, cause } = pendingAction;

    try {
      if (type === 'publish') {
        const updatedCause = await CauseService.publishCause(cause.id);
        setCauses((current) =>
          current.map((item) => (item.id === updatedCause.id ? updatedCause : item))
        );
      }

      if (type === 'unpublish') {
        const updatedCause = await CauseService.unpublishCause(cause.id);
        setCauses((current) =>
          current.map((item) => (item.id === updatedCause.id ? updatedCause : item))
        );
      }

      if (type === 'archive') {
        const updatedCause = await CauseService.archiveCause(cause.id);
        setCauses((current) =>
          current.map((item) => (item.id === updatedCause.id ? updatedCause : item))
        );
      }

      if (type === 'delete') {
        await CauseService.deleteCause(cause.id);
        setCauses((current) => current.filter((item) => item.id !== cause.id));
      }

      await AuditLogService.recordEntry({
        module: 'causes',
        action: type,
        description: `${cause.title} action executed: ${type}.`,
        actorEmail: user?.email || 'admin@local',
        targetId: cause.id,
        targetLabel: cause.title,
      });
      showToast(`Cause ${type} completed.`);
    } catch (actionError) {
      console.error(actionError);
      setError(`Failed to ${type} cause.`);
    } finally {
      setPendingAction(null);
    }
  };

  if (loading) {
    return <LoadingState label="Loading causes…" />;
  }

  return (
    <AdminPage
      title="Causes"
      description="Manage cause visibility, metadata, and publishing lifecycle from a single list."
      action={
        canManage ? (
          <Button variant="contained" onClick={() => navigate('/causes/new')}>
            Create Cause
          </Button>
        ) : null
      }
    >
      {error ? <Alert severity="error">{error}</Alert> : null}

      <SectionCard
        title="Cause library"
        description="Search and filter the catalog, then jump into full cause details or editing."
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Search causes"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(event) =>
                  setStatusFilter(event.target.value as 'all' | Cause['status'])
                }
              >
                <MenuItem value="all">All statuses</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="paused">Paused</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {filteredCauses.length === 0 ? (
          <EmptyState
            title="No causes match the current filters"
            description="Try a broader search or create a new cause to get started."
            action={
              canManage ? (
                <Button variant="contained" onClick={() => navigate('/causes/new')}>
                  Create Cause
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <Box sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Cause</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Goal</TableCell>
                    <TableCell align="right">Raised</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCauses.map((cause) => (
                    <TableRow key={cause.id} hover>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography fontWeight={600}>{cause.title}</Typography>
                          {cause.titleEn || cause.titleAr ? (
                            <Typography variant="body2" color="text.secondary">
                              {[cause.titleEn, cause.titleAr].filter(Boolean).join(' / ')}
                            </Typography>
                          ) : null}
                          <Typography variant="body2" color="text.secondary">
                            {cause.shortDescription || cause.description.slice(0, 84)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" fontWeight={600}>
                            {cause.externalProgramId || cause.id}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Rank {cause.rank ?? 'Not set'}
                          </Typography>
                          {cause.mealType ? (
                            <Typography variant="body2" color="text.secondary">
                              {cause.mealType}
                            </Typography>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <StatusChip value={cause.status} />
                          {cause.featured ? <StatusChip value="featured" /> : null}
                          {cause.shareable ? <StatusChip value="shareable" /> : null}
                        </Stack>
                      </TableCell>
                      <TableCell>{cause.category || 'General'}</TableCell>
                      <TableCell align="right">{formatCurrency(cause.goalAmount)}</TableCell>
                      <TableCell align="right">{formatCurrency(cause.currentAmount)}</TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                          flexWrap="wrap"
                        >
                          <Button
                            variant="text"
                            onClick={() => navigate(`/causes/${cause.id}`)}
                          >
                            View
                          </Button>
                          {canManage ? (
                            <>
                              <Button
                                variant="text"
                                onClick={() => navigate(`/causes/${cause.id}/edit`)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="text"
                                onClick={() =>
                                  setPendingAction({
                                    type:
                                      cause.status === 'published'
                                        ? 'unpublish'
                                        : 'publish',
                                    cause,
                                  })
                                }
                              >
                                {cause.status === 'published' ? 'Unpublish' : 'Publish'}
                              </Button>
                              <Button
                                variant="text"
                                color="warning"
                                onClick={() =>
                                  setPendingAction({ type: 'archive', cause })
                                }
                              >
                                Archive
                              </Button>
                              <Button
                                variant="text"
                                color="error"
                                onClick={() =>
                                  setPendingAction({ type: 'delete', cause })
                                }
                              >
                                Delete
                              </Button>
                            </>
                          ) : null}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {filteredCauses.length} causes found
              </Typography>
              <Pagination
                page={page}
                count={pageCount}
                onChange={(_, nextPage) => setPage(nextPage)}
                color="primary"
              />
            </Stack>
          </>
        )}
      </SectionCard>

      <ConfirmDialog
        open={Boolean(pendingAction)}
        title={
          pendingAction
            ? `${pendingAction.type.charAt(0).toUpperCase()}${pendingAction.type.slice(1)} cause`
            : 'Confirm action'
        }
        description={
          pendingAction
            ? `Are you sure you want to ${pendingAction.type} "${pendingAction.cause.title}"?`
            : ''
        }
        confirmLabel={
          pendingAction
            ? `${pendingAction.type.charAt(0).toUpperCase()}${pendingAction.type.slice(1)}`
            : 'Confirm'
        }
        confirmColor={
          pendingAction?.type === 'delete'
            ? 'error'
            : pendingAction?.type === 'archive'
              ? 'warning'
              : 'primary'
        }
        onClose={() => setPendingAction(null)}
        onConfirm={handleConfirmAction}
      />
    </AdminPage>
  );
};

export default CausesListPage;
